<!--
    MAIN DYNAMIC DASHBOARD FOR CONDUCTING CONTOUR CHECK, ANALYSIS AND GENERATING REPORT.
    COMPONENT PART OF © AREA 19, OCG WEB APP.
    DEVELOPED IN MONTREAL, QC, CANADA.
    JULY 2023.
  -->

<script>
import GeneralPurposeContainer from '@src/components/GeneralPurposeContainer.vue';

import AnalysisNavigationBar from '../components/AnalysisNavigationBar.vue';
import FlowComponent from '../components/FlowComponent.vue';
import FunctionComponent from '../components/FunctionComponent.vue';
import ReportMaker from '../components/report-generator/ReportMaker.vue';
import VersionNumber from '../components/VersionNumber.vue';
import ViewerComponent from '../components/ViewerComponent.vue';
import { useAppState } from '../store/ocg-app-state';
import { useGeneralPurposeOverlayManager } from '../store/overlay-management/overlay-manager';
import GraphicalAnalysis from './GraphicalAnalysis.vue';
import QualityCheck from './QualityCheck.vue';

export default {
  components: {
    AnalysisNavigationBar,
    QualityCheck,
    ReportMaker,
    GraphicalAnalysis,
    ViewerComponent,
    VersionNumber,
    FunctionComponent,
    FlowComponent,
    GeneralPurposeContainer,
  },
  data() {
    const overlayManager = useGeneralPurposeOverlayManager();

    return {
      // Data properties
      appState: useAppState(),
      overlayManager,
      overlayState: overlayManager.getGeneralPurposeOverlayState(),
    };
  },
  computed: {
    isInlineOverlayOpen() {
      return (
        this.overlayState.positionType === 'inline' && this.overlayState.isOpen
      );
    },
    currentView() {
      return this.appState.currentView;
    },
  },
  watch: {
    currentView: {
      immediate: true,
      handler(newView) {
        if (newView === 'ReportMaker') {
          this.overlayManager.showGeneralPurposeOverlay({ viewerType: 'clipboard' });
        }
        else if (newView === 'GraphicalAnalysis') {
          this.overlayManager.closeGeneralPurposeOverlay();
        }
      },
    },
  },
  created() {
    this.overlayManager.setGeneralPurposePositionType('inline');

    // Set the time of day and update the container class accordingly
    if (this.isAnalysisRoute) {
      this.appState.setView('QualityCheck');
    }
  },
};
</script>

<template>
  <div
    class="main-analysis-layout flex w-full h-full"
    :class="{ 'main-analysis-layout--overlay-open': isInlineOverlayOpen }"
  >
    <div
      class="main-analysis-content"
      :class="{ 'main-analysis-content--overlay-open': isInlineOverlayOpen }"
    >
      <transition name="fade" mode="out-in">
        <KeepAlive>
          <component :is="appState.currentView" />
        </KeepAlive>
      </transition>
    </div>

    <div
      class="general-purpose-pane"
      :class="{ 'general-purpose-pane--open': isInlineOverlayOpen }"
    >
      <GeneralPurposeContainer id="Contours" />
    </div>
  </div>

  <VersionNumber :margin-bot="34" />
</template>

<style scoped>
.main-analysis-layout {
  --general-purpose-inline-width: 412px;
  min-height: 0;
  column-gap: 0;
  transition: column-gap 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.main-analysis-layout--overlay-open {
  column-gap: 0.5rem;
}

.main-analysis-content {
  height: 100%;
  min-width: 0;
  min-height: 0;
  flex: 1 1 auto;
  transition: flex-basis 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.main-analysis-content--overlay-open {
  flex: 1 1 calc(100% - var(--general-purpose-inline-width));
}

.general-purpose-pane {
  display: flex;
  height: 100%;
  width: 0;
  flex: 0 0 0;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  transition:
    width 0.4s cubic-bezier(0.25, 0.8, 0.25, 1),
    flex-basis 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.general-purpose-pane--open {
  width: var(--general-purpose-inline-width);
  flex: 0 0 var(--general-purpose-inline-width);
}

/* Styles for the fade-in animation */
.fade-enter-from {
  opacity: 0;
}
.fade-enter-to {
  opacity: 1;
}

.fade-enter-active {
  transition: all 0.5s ease;
}
</style>
